import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useViewerId from "@/api/user/use-viewer-id";
import useDeleteTeamMember from "@babackapp/private-api-client/client/use-delete-team-member";
import useUpdateTeamMemberRole from "@babackapp/private-api-client/client/use-update-team-member-role";
import { ShopMemberDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Avatar, AvatarFallback } from "@babackapp/ui/components/avatar";
import { Badge } from "@babackapp/ui/components/badge";
import { RoleSelect } from "./role-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";

type TeamMemberListItemProps = {
  member: ShopMemberDto;
};

export function TeamMemberListItem({ member }: TeamMemberListItemProps) {
  const { data: currentUserId } = useViewerId();
  const { data: shop } = useCurrentShopSuspense();
  const t = useTranslation();
  const locations = shop?.returnToStore.stores?.filter(
    (location) => location.id != null,
  );

  const { mutateAsync: updateRole } = useUpdateTeamMemberRole(
    shop.id,
    member.userId,
  );

  const { mutateAsync: deleteMember } = useDeleteTeamMember(
    shop.id,
    member.userId,
  );

  const onChange = (roleId: ShopMemberDto["roleId"], location?: string) => {
    return updateRole({ roleId, storeId: location });
  };

  const title = member.user?.firstName
    ? `${member.user?.firstName} ${member.user?.lastName ?? ""}`.trim()
    : member.user?.email;

  const subtitle = member.user?.firstName
    ? member.user?.email
    : member.user?.id;

  return (
    <div className="flex items-center gap-4 p-2">
      <Avatar className="border border-border">
        <AvatarFallback>{title?.charAt(0).toUpperCase() ?? ""}</AvatarFallback>
      </Avatar>
      <div className="flex flex-col gap-1 flex-1">
        <span className="font-medium flex gap-2">
          {title}{" "}
          {member.roleId === "OWNER" && (
            <Badge className="ml-2">{t("role.owner.name")}</Badge>
          )}
        </span>
        <span className="text-muted-foreground text-sm">{subtitle}</span>
      </div>
      {member.roleId !== "OWNER" && currentUserId !== member.userId ? (
        <RoleSelect
          value={member.roleId as ShopMemberDto["roleId"]}
          onChange={onChange}
          onRemove={deleteMember}
        />
      ) : null}
      {member.roleId.toLowerCase() === "store" && (
        <Select
          onValueChange={(location) => onChange(member.roleId, location)}
          defaultValue={member.storeId || undefined}
        >
          <SelectTrigger className="h-10 w-fit">
            <SelectValue placeholder="Select a store" />
          </SelectTrigger>
          <SelectContent>
            {locations?.map((location) => (
              <SelectItem key={location.id} value={location.id!}>
                {location.address?.companyName || "n/a"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
